import {
   IAllLeadSources,
   ILeadStageItem,
   IPondListItem,
   IStageMapping,
   IStageMappingConfig,
   IStageMappingValue,
   PaginatedResponse,
   PaginationQueryParams,
} from '@forwardflow/types'
import { apiGet, apiPatch } from '../Axios/configs'
import { ResponseSuccess } from '@forwardflow/types'
import applyPaginationQueryParams from '@forwardflow/helpers/utils/applyPaginationQueryParams'

export default class StageMappingService {
   // Get Stage mapping config
   static async getStageMappingConfig() {
      const data = await apiGet<ResponseSuccess<IStageMappingConfig>>({
         apiPath: `/stage-mappings/configs`,
      })
      return data.data
   }

   // Get Stage mapping
   static async getStageMapping() {
      const data = await apiGet<ResponseSuccess<IStageMapping>>({
         apiPath: `/stage-mappings`,
      })
      return data.data
   }

   // Get list of fub sources
   static async getFubSources(queryParams: PaginationQueryParams) {
      const response = await apiGet<ResponseSuccess<IAllLeadSources[]>>({
         apiPath: `/fub-data/sources?${applyPaginationQueryParams(
            queryParams
         )}`,
      })
      return response
   }

   // Get list of fub tags
   static async getFubTags(queryParams: PaginationQueryParams) {
      const response = await apiGet<ResponseSuccess<string[]>>({
         apiPath: `/fub-data/tags?${applyPaginationQueryParams(queryParams)}`,
      })
      return response
   }

   // Get list of fub leads
   static async getFubStages(queryParams: PaginationQueryParams) {
      const response = await apiGet<PaginatedResponse<ILeadStageItem[]>>({
         apiPath: `/fub-data/stages?${applyPaginationQueryParams(queryParams)}`,
      })
      return response
   }

   // Get list of fub ponds

   static async getFubPonds(queryParams: PaginationQueryParams) {
      const response = await apiGet<ResponseSuccess<IPondListItem[]>>({
         apiPath: `/fub-data/ponds?${applyPaginationQueryParams(queryParams)}`,
      })
      return response
   }

   // Update Stage group
   static async updateStageMapping(stageMappingValue: IStageMappingValue) {
      const response = await apiPatch<ResponseSuccess<IStageMapping>>({
         apiPath: `/stage-mappings/combined`,
         data: {
            isFFTagActive: stageMappingValue?.isFFTagActive ?? false,
            stageMappings: [
               {
                  stageGroup: 'Appointment Scheduled',
                  multiStageEnabled: false,
                  stages: [
                     {
                        outboundMappings: {
                           stages: stageMappingValue?.AppointmentScheduledStage,
                           tags: stageMappingValue?.AppointmentScheduledTags,
                        },
                     },
                  ],
                  inboundMappings: [
                     {
                        stages: stageMappingValue?.AppointmentScheduledStage,
                        tags: stageMappingValue?.AppointmentScheduledTags,
                     },
                  ],
               },
               {
                  stageGroup: 'Nurturing',
                  multiStageEnabled: true,
                  stages: [
                     {
                        name: stageMappingValue?.HotProspectStageName,
                        outboundMappings: {
                           stages: stageMappingValue?.HotProspectStage,
                           tags: stageMappingValue?.HotProspectTags,
                        },
                     },
                     {
                        name: stageMappingValue?.WarmProspectStageName,
                        outboundMappings: {
                           stages: stageMappingValue?.WarmProspectStage,
                           tags: stageMappingValue?.WarmProspectTags,
                        },
                     },
                     {
                        name: stageMappingValue?.ColdProspectStageName,
                        outboundMappings: {
                           stages: stageMappingValue?.ColdProspectStage,
                           tags: stageMappingValue?.ColdProspectTags,
                        },
                     },
                     {
                        name: stageMappingValue?.LongTermProspectStageName,
                        outboundMappings: {
                           stages: stageMappingValue?.LongTermProspectStage,
                           tags: stageMappingValue?.LongTermProspectTags,
                        },
                     },
                  ],
                  inboundMappings: [
                     {
                        name: stageMappingValue?.HotProspectStageName,
                        stages: stageMappingValue?.HotProspectStage,
                        tags: stageMappingValue?.HotProspectTags,
                     },
                     {
                        name: stageMappingValue?.WarmProspectStageName,
                        stages: stageMappingValue?.WarmProspectStage,
                        tags: stageMappingValue?.WarmProspectTags,
                     },
                     {
                        name: stageMappingValue?.ColdProspectStageName,
                        stages: stageMappingValue?.ColdProspectStage,
                        tags: stageMappingValue?.ColdProspectTags,
                     },
                     {
                        name: stageMappingValue?.LongTermProspectStageName,
                        stages: stageMappingValue?.LongTermProspectStage,
                        tags: stageMappingValue?.LongTermProspectTags,
                     },
                  ],
               },
               {
                  stageGroup: 'Appointment Missed',
                  multiStageEnabled: false,
                  stages: [
                     {
                        outboundMappings: {
                           stages: stageMappingValue?.AppointmentMissedStage,
                           tags: stageMappingValue?.AppointmentMissedTags,
                        },
                     },
                  ],
                  inboundMappings: [
                     {
                        stages: stageMappingValue?.AppointmentMissedStage,
                        tags: stageMappingValue?.AppointmentMissedTags,
                     },
                  ],
               },
               {
                  stageGroup: 'Stop Stage',
                  multiStageEnabled: true,
                  stages: [
                     {
                        name: stageMappingValue?.DoNotContactStageName,
                        outboundMappings: {
                           stages: stageMappingValue?.DoNotContactStage,
                           tags: stageMappingValue?.DoNotContactTags,
                        },
                     },
                     {
                        name: stageMappingValue?.HasAnotherAgentStageName,
                        outboundMappings: {
                           stages: stageMappingValue?.HasAnotherAgentStage,
                           tags: stageMappingValue?.HasAnotherAgentTags,
                        },
                     },
                     {
                        name: stageMappingValue?.DealClosedStageName,
                        outboundMappings: {
                           stages: stageMappingValue?.DealClosedStage,
                           tags: stageMappingValue?.DealClosedTags,
                        },
                     },
                     {
                        name: stageMappingValue?.ActiveClientStageName,
                        outboundMappings: {
                           stages: stageMappingValue?.ActiveClientStage,
                           tags: stageMappingValue?.ActiveClientTags,
                        },
                     },
                     {
                        name: stageMappingValue?.UnderContractStageName,
                        outboundMappings: {
                           stages: stageMappingValue?.UnderContractStage,
                           tags: stageMappingValue?.UnderContractTags,
                        },
                     },
                  ],
                  inboundMappings: [
                     {
                        name: stageMappingValue?.DoNotContactStageName,
                        stages: stageMappingValue?.DoNotContactStage,
                        tags: stageMappingValue?.DoNotContactTags,
                     },
                     {
                        name: stageMappingValue?.HasAnotherAgentStageName,
                        stages: stageMappingValue?.HasAnotherAgentStage,
                        tags: stageMappingValue?.HasAnotherAgentTags,
                     },
                     {
                        name: stageMappingValue?.DealClosedStageName,
                        stages: stageMappingValue?.DealClosedStage,
                        tags: stageMappingValue?.DealClosedTags,
                     },
                     {
                        name: stageMappingValue?.ActiveClientStageName,
                        stages: stageMappingValue?.ActiveClientStage,
                        tags: stageMappingValue?.ActiveClientTags,
                     },
                     {
                        name: stageMappingValue?.UnderContractStageName,
                        stages: stageMappingValue?.UnderContractStage,
                        tags: stageMappingValue?.UnderContractTags,
                     },
                  ],
               },
            ],
         },
      })
      return response
   }
}
